import { PDFDocument, PDFTextField, StandardFonts, rgb } from 'pdf-lib';

export type TecFormId = 'te7' | 'te9' | 'pe2' | 'pe3';

export interface TecFormPrefillValues {
  fullName: string;
  firstName: string;
  lastName: string;
  company: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  postcode: string;
  email: string;
  phone: string;
  vrm: string;
  pcnNumber: string;
  submissionDate: string;
  submissionDay: string;
  submissionMonth: string;
  submissionYear: string;
  reason: string;
  statement: string;
}

export interface TecPdfFillResult {
  bytes: Uint8Array;
  wasFilled: boolean;
  filledFields: string[];
  usedOverlay: boolean;
}

const toLower = (value: string) => value.toLowerCase();
const includesAny = (value: string, terms: string[]) => terms.some((term) => value.includes(term));
const includesAll = (value: string, terms: string[]) => terms.every((term) => value.includes(term));

const shouldSkipField = (name: string) =>
  includesAny(name, ['signature', 'sign', 'tick', 'checkbox', 'ground', 'button']);

// Exact field name mappings for known PE/TE form fields
const EXACT_FIELD_MAPPINGS: Record<string, keyof TecFormPrefillValues> = {
  'name': 'fullName',
  'your name': 'fullName',
  'full name': 'fullName',
  'fullname': 'fullName',
  'respondent name': 'fullName',
  'applicant name': 'fullName',
  'address': 'addressLine1',
  'address line 1': 'addressLine1',
  'addressline1': 'addressLine1',
  'address line 2': 'addressLine2',
  'addressline2': 'addressLine2',
  'town': 'city',
  'city': 'city',
  'post code': 'postcode',
  'postcode': 'postcode',
  'telephone': 'phone',
  'phone': 'phone',
  'phone number': 'phone',
  'tel': 'phone',
  'email': 'email',
  'email address': 'email',
  'e-mail': 'email',
  'vehicle registration': 'vrm',
  'vrm': 'vrm',
  'vehicle reg': 'vrm',
  'reg no': 'vrm',
  'registration': 'vrm',
  'pcn number': 'pcnNumber',
  'pcn ref': 'pcnNumber',
  'pcn': 'pcnNumber',
  'penalty charge notice number': 'pcnNumber',
  'reference': 'pcnNumber',
  'case number': 'pcnNumber',
  'date': 'submissionDate',
  'todays date': 'submissionDate',
  'statement': 'reason',
  'grounds': 'reason',
  'reason': 'reason',
  'explanation': 'reason',
  'day': 'submissionDay',
  'month': 'submissionMonth',
  'year': 'submissionYear',
  'company': 'company',
  'organisation': 'company',
  'organization': 'company',
  'surname': 'lastName',
  'last name': 'lastName',
  'family name': 'lastName',
  'first name': 'firstName',
  'forename': 'firstName',
  'given name': 'firstName',
};

const resolveFieldValue = (name: string, values: TecFormPrefillValues): string | null => {
  if (shouldSkipField(name)) return null;

  // Try exact match first (case-insensitive)
  const exactKey = EXACT_FIELD_MAPPINGS[name];
  if (exactKey) return values[exactKey] || null;

  // Fuzzy matching as fallback
  if (includesAny(name, ['postcode', 'post code', 'zip'])) return values.postcode;
  if (includesAny(name, ['email', 'e-mail'])) return values.email;
  if (includesAny(name, ['telephone', 'phone', 'tel'])) return values.phone;

  if (includesAll(name, ['vehicle', 'reg']) || includesAny(name, ['vrm', 'vehicle registration'])) return values.vrm;
  if (includesAny(name, ['pcn']) || (includesAny(name, ['penalty', 'notice']) && includesAny(name, ['number', 'no', 'ref']))) {
    return values.pcnNumber;
  }

  if (includesAny(name, ['company', 'organisation', 'organization', 'business'])) return values.company;
  if (includesAny(name, ['surname', 'last name', 'family name'])) return values.lastName;
  if (includesAny(name, ['first name', 'forename', 'given name'])) return values.firstName;
  if (includesAny(name, ['name']) && !includesAny(name, ['authority', 'council'])) return values.fullName;

  if (includesAny(name, ['address line 2', 'address2', 'address 2'])) return values.addressLine2;
  if (includesAny(name, ['address line 1', 'address1', 'address 1', 'address'])) return values.addressLine1;
  if (includesAny(name, ['town', 'city'])) return values.city;

  if (includesAny(name, ['reason', 'explain', 'explanation', 'statement', 'details', 'grounds'])) {
    return values.reason || values.statement;
  }

  if (includesAny(name, ['date'])) return values.submissionDate;
  if (includesAny(name, ['day'])) return values.submissionDay;
  if (includesAny(name, ['month'])) return values.submissionMonth;
  if (includesAny(name, ['year'])) return values.submissionYear;

  return null;
};

const sanitizeValue = (value?: string | null): string => (value || '').trim();

const getSafePrefillValue = (value?: string | null) => {
  const next = sanitizeValue(value);
  return next.length > 0 ? next : '';
};

interface OverlayField {
  key: keyof TecFormPrefillValues;
  x: number;
  y: number;
  fontSize?: number;
  maxWidth?: number;
  lineHeight?: number;
  transform?: 'uppercase';
}

interface OverlayConfig {
  pageIndex: number;
  fields: OverlayField[];
  signature?: {
    pageIndex?: number;
    x: number;
    y: number;
    width: number;
    height: number;
  };
}

// NOTE: Overlay coordinates are based on A4 (595x842 points).
// PE3 = Statutory Declaration, PE2 = Out of Time Application
// TE9 = Witness Statement, TE7 = Out of Time Application
// These coordinates have been tuned based on typical government form layouts.
// Use debug: true option when calling fillTecPdf to discover actual field positions.
const FORM_OVERLAYS: Record<TecFormId, OverlayConfig> = {
  te9: {
    pageIndex: 0,
    fields: [
      // TE9 Witness Statement - typically has personal details at top
      { key: 'fullName', x: 145, y: 710, fontSize: 10 },
      { key: 'addressLine1', x: 145, y: 690, fontSize: 10 },
      { key: 'addressLine2', x: 145, y: 675, fontSize: 10 },
      { key: 'city', x: 145, y: 660, fontSize: 10 },
      { key: 'postcode', x: 145, y: 645, fontSize: 10 },
      { key: 'phone', x: 145, y: 620, fontSize: 10 },
      { key: 'email', x: 300, y: 620, fontSize: 10 },
      { key: 'pcnNumber', x: 145, y: 595, fontSize: 10 },
      { key: 'vrm', x: 300, y: 595, fontSize: 10, transform: 'uppercase' },
      { key: 'reason', x: 55, y: 480, fontSize: 10, maxWidth: 500, lineHeight: 14 },
      { key: 'submissionDate', x: 145, y: 180, fontSize: 10 },
    ],
    signature: {
      x: 140,
      y: 160,
      width: 220,
      height: 50,
    }
  },
  te7: {
    pageIndex: 0,
    fields: [
      // TE7 Out of Time - similar layout to TE9
      { key: 'fullName', x: 145, y: 710, fontSize: 10 },
      { key: 'addressLine1', x: 145, y: 690, fontSize: 10 },
      { key: 'addressLine2', x: 145, y: 675, fontSize: 10 },
      { key: 'city', x: 145, y: 660, fontSize: 10 },
      { key: 'postcode', x: 145, y: 645, fontSize: 10 },
      { key: 'phone', x: 145, y: 620, fontSize: 10 },
      { key: 'email', x: 300, y: 620, fontSize: 10 },
      { key: 'pcnNumber', x: 145, y: 595, fontSize: 10 },
      { key: 'vrm', x: 300, y: 595, fontSize: 10, transform: 'uppercase' },
      { key: 'reason', x: 55, y: 450, fontSize: 10, maxWidth: 500, lineHeight: 14 },
      { key: 'submissionDate', x: 145, y: 180, fontSize: 10 },
    ],
    signature: {
      x: 140,
      y: 160,
      width: 220,
      height: 50,
    }
  },
  pe3: {
    pageIndex: 0,
    fields: [
      // PE3 Statutory Declaration - standard TEC layout
      // Section 1: "To the Traffic Enforcement Centre" header, then applicant details
      { key: 'fullName', x: 55, y: 695, fontSize: 10 },
      { key: 'addressLine1', x: 55, y: 680, fontSize: 10 },
      { key: 'addressLine2', x: 55, y: 665, fontSize: 10 },
      { key: 'city', x: 55, y: 650, fontSize: 10 },
      { key: 'postcode', x: 200, y: 650, fontSize: 10 },
      { key: 'phone', x: 55, y: 630, fontSize: 10 },
      { key: 'email', x: 200, y: 630, fontSize: 10 },
      // Section 2: PCN/Case details
      { key: 'pcnNumber', x: 180, y: 600, fontSize: 10 },
      { key: 'vrm', x: 400, y: 600, fontSize: 10, transform: 'uppercase' },
      // Section 3: Declaration/Statement
      { key: 'reason', x: 55, y: 500, fontSize: 10, maxWidth: 500, lineHeight: 14 },
      // Section 4: Date and signature at bottom
      { key: 'submissionDay', x: 90, y: 230, fontSize: 10 },
      { key: 'submissionMonth', x: 155, y: 230, fontSize: 10 },
      { key: 'submissionYear', x: 245, y: 230, fontSize: 10 },
    ]
  },
  pe2: {
    pageIndex: 0,
    fields: [
      // PE2 Out of Time Application - explains why late
      { key: 'fullName', x: 55, y: 700, fontSize: 10 },
      { key: 'addressLine1', x: 55, y: 685, fontSize: 10 },
      { key: 'addressLine2', x: 55, y: 670, fontSize: 10 },
      { key: 'city', x: 55, y: 655, fontSize: 10 },
      { key: 'postcode', x: 200, y: 655, fontSize: 10 },
      { key: 'phone', x: 55, y: 635, fontSize: 10 },
      { key: 'email', x: 200, y: 635, fontSize: 10 },
      { key: 'pcnNumber', x: 180, y: 605, fontSize: 10 },
      { key: 'vrm', x: 400, y: 605, fontSize: 10, transform: 'uppercase' },
      // Reason for being out of time
      { key: 'reason', x: 55, y: 480, fontSize: 10, maxWidth: 500, lineHeight: 14 },
      { key: 'submissionDay', x: 90, y: 220, fontSize: 10 },
      { key: 'submissionMonth', x: 155, y: 220, fontSize: 10 },
      { key: 'submissionYear', x: 245, y: 220, fontSize: 10 },
    ]
  },
};

const wrapText = (text: string, maxWidth: number, font: any, fontSize: number): string[] => {
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let current = '';
  for (const word of words) {
    const next = current ? `${current} ${word}` : word;
    const width = font.widthOfTextAtSize(next, fontSize);
    if (width <= maxWidth) {
      current = next;
    } else {
      if (current) lines.push(current);
      current = word;
    }
  }
  if (current) lines.push(current);
  return lines;
};

const applyOverlay = async (pdfDoc: PDFDocument, formId: TecFormId, values: TecFormPrefillValues) => {
  const overlay = FORM_OVERLAYS[formId];
  if (!overlay) return false;

  const pages = pdfDoc.getPages();
  const page = pages[overlay.pageIndex] || pages[0];
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const defaultSize = 10;

  overlay.fields.forEach((field) => {
    const raw = values[field.key];
    const next = getSafePrefillValue(raw);
    if (!next) return;
    const fontSize = field.fontSize ?? defaultSize;
    const text = field.transform === 'uppercase' ? next.toUpperCase() : next;

    if (field.maxWidth) {
      const lines = wrapText(text, field.maxWidth, font, fontSize);
      const lineHeight = field.lineHeight ?? fontSize + 2;
      lines.forEach((line, index) => {
        page.drawText(line, {
          x: field.x,
          y: field.y - index * lineHeight,
          size: fontSize,
          font,
          color: rgb(0, 0, 0),
        });
      });
      return;
    }

    page.drawText(text, {
      x: field.x,
      y: field.y,
      size: fontSize,
      font,
      color: rgb(0, 0, 0),
    });
  });

  return true;
};

const decodeDataUrl = (dataUrl: string): Uint8Array => {
  const base64 = dataUrl.includes(',') ? dataUrl.split(',')[1] : dataUrl;
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
};

const applySignature = async (pdfDoc: PDFDocument, formId: TecFormId, dataUrl: string) => {
  const overlay = FORM_OVERLAYS[formId];
  const signature = overlay?.signature;
  if (!signature) return false;

  const pages = pdfDoc.getPages();
  const page = pages[signature.pageIndex ?? overlay.pageIndex] || pages[0];
  const imageBytes = decodeDataUrl(dataUrl);
  const image = dataUrl.startsWith('data:image/jpeg') || dataUrl.startsWith('data:image/jpg')
    ? await pdfDoc.embedJpg(imageBytes)
    : await pdfDoc.embedPng(imageBytes);

  const size = image.scale(1);
  const scale = Math.min(signature.width / size.width, signature.height / size.height);
  const drawWidth = size.width * scale;
  const drawHeight = size.height * scale;
  const offsetX = signature.x + (signature.width - drawWidth) / 2;
  const offsetY = signature.y + (signature.height - drawHeight) / 2;

  page.drawImage(image, {
    x: offsetX,
    y: offsetY,
    width: drawWidth,
    height: drawHeight,
  });

  return true;
};

export interface FillTecPdfOptions {
  forceOverlay?: boolean;
  debug?: boolean;
  signatureDataUrl?: string;
}

export const fillTecPdf = async (
  sourceUrl: string,
  values: TecFormPrefillValues,
  formId?: TecFormId,
  options?: FillTecPdfOptions
): Promise<TecPdfFillResult> => {
  const resolvePdfUrl = (url: string) => {
    if (url.startsWith('blob:') || url.startsWith('data:')) return url;
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    const normalized = url.startsWith('/') ? url.slice(1) : url;
    const baseHref = typeof document !== 'undefined'
      ? document.querySelector('base')?.href
      : undefined;
    if (baseHref) return new URL(normalized, baseHref).toString();
    const base = (import.meta as any)?.env?.BASE_URL || '/';
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const normalizedBase = base.startsWith('/') ? base : `/${base}`;
    if (origin) {
      const baseUrl = new URL(normalizedBase.endsWith('/') ? normalizedBase : `${normalizedBase}/`, origin);
      return new URL(normalized, baseUrl).toString();
    }
    return `${normalizedBase}${normalized}`;
  };

  const resolvedUrl = resolvePdfUrl(sourceUrl);
  const candidates = new Set<string>([resolvedUrl, sourceUrl]);
  if (typeof window !== 'undefined') {
    const normalized = sourceUrl.startsWith('/') ? sourceUrl.slice(1) : sourceUrl;
    candidates.add(new URL(normalized, `${window.location.origin}/`).toString());
    const base = (import.meta as any)?.env?.BASE_URL || '/';
    const basePrefix = base.startsWith('/') ? base : `/${base}`;
    candidates.add(new URL(normalized, `${window.location.origin}${basePrefix.endsWith('/') ? basePrefix : `${basePrefix}/`}`).toString());
  }

  const isPdfBytes = (bytes: Uint8Array) => {
    const head = bytes.slice(0, Math.min(1024, bytes.length));
    let text = '';
    for (let i = 0; i < head.length; i += 1) {
      text += String.fromCharCode(head[i]);
    }
    return text.includes('%PDF-');
  };

  let sourceBytes: Uint8Array | null = null;
  let resolvedCandidate = '';
  let lastError: string | null = null;

  for (const candidate of candidates) {
    try {
      const response = await fetch(candidate, { cache: 'no-store' });
      if (!response.ok) {
        lastError = `Failed to fetch form (${response.status}) from ${candidate}`;
        continue;
      }
      const contentType = response.headers.get('content-type') || 'unknown';
      const bytes = new Uint8Array(await response.arrayBuffer());
      if (!bytes.length) {
        lastError = `Empty response from ${candidate} (content-type: ${contentType})`;
        continue;
      }
      if (!isPdfBytes(bytes)) {
        const preview = new TextDecoder('utf-8')
          .decode(bytes.slice(0, 200))
          .replace(/\s+/g, ' ')
          .trim();
        lastError = `Non-PDF response from ${candidate} (content-type: ${contentType}, bytes: ${bytes.length}). Preview: "${preview.slice(0, 120)}"`;
        continue;
      }
      sourceBytes = bytes;
      resolvedCandidate = candidate;
      break;
    } catch (err: any) {
      lastError = `Failed to fetch ${candidate}: ${err?.message || err}`;
    }
  }

  if (!sourceBytes) {
    throw new Error(lastError || `Failed to load PDF from ${resolvedUrl}. No valid PDF header found.`);
  }
  let pdfDoc: PDFDocument;
  try {
    pdfDoc = await PDFDocument.load(sourceBytes, { ignoreEncryption: true });
  } catch (err: any) {
    const msg = err?.message || err;
    throw new Error(`Failed to parse PDF from ${resolvedCandidate}: ${msg}`);
  }
  const form = pdfDoc.getForm();
  const fields = form.getFields();

  // Debug: Log all field names to help with mapping
  if (options?.debug) {
    console.log(`[TEC PDF Debug] Form ${formId || 'unknown'} has ${fields.length} fields:`);
    fields.forEach((field) => {
      const fieldType = field.constructor.name;
      const fieldName = field.getName();
      console.log(`  - "${fieldName}" (${fieldType})`);
    });
    console.log('[TEC PDF Debug] Values to fill:', values);
  }

  const filledFields: string[] = [];
  for (const field of fields) {
    if (!(field instanceof PDFTextField)) continue;
    const fieldName = toLower(field.getName());
    const value = resolveFieldValue(fieldName, values);
    const safeValue = getSafePrefillValue(value);
    if (!safeValue) {
      if (options?.debug) {
        console.log(`[TEC PDF Debug] Field "${field.getName()}" - no matching value`);
      }
      continue;
    }
    field.setText(safeValue);
    filledFields.push(field.getName());
    if (options?.debug) {
      console.log(`[TEC PDF Debug] Field "${field.getName()}" filled with "${safeValue}"`);
    }
  }

  if (filledFields.length > 0) {
    form.updateFieldAppearances();
  }

  let usedOverlay = false;
  if (formId && (options?.forceOverlay || filledFields.length === 0)) {
    if (options?.debug) {
      console.log(`[TEC PDF Debug] Applying overlay for ${formId} (forceOverlay=${options?.forceOverlay}, filledFields=${filledFields.length})`);
    }
    usedOverlay = await applyOverlay(pdfDoc, formId, values);
  }

  if (formId && options?.signatureDataUrl) {
    await applySignature(pdfDoc, formId, options.signatureDataUrl);
  }

  const bytes = await pdfDoc.save();
  if (options?.debug) {
    console.log(`[TEC PDF Debug] Result: wasFilled=${filledFields.length > 0}, filledFields=${filledFields.join(', ')}, usedOverlay=${usedOverlay}`);
  }
  return { bytes, wasFilled: filledFields.length > 0, filledFields, usedOverlay };
};

export const downloadBytes = (bytes: Uint8Array, filename: string, mimeType: string) => {
  const blob = new Blob([bytes], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
};
